import { useCallback, useEffect, useState } from 'react';

/**
 * Light, dark, or whatever the system says. The choice is a per-browser
 * convenience, so it lives in localStorage; when storage is unavailable the
 * portal simply follows the system. The stylesheet does the rest: it keys on
 * `data-theme` on <html> and on the system preference (see app.css).
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type Scheme = 'light' | 'dark';

const KEY = 'portal:theme';
const QUERY = '(prefers-color-scheme: dark)';

export function readPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function writePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, preference);
  } catch {
    // Not remembered; the choice still holds until the page is closed.
  }
}

export function resolveScheme(preference: ThemePreference, systemDark: boolean): Scheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

export function applyPreference(preference: ThemePreference): void {
  if (preference === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = preference;
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches === true;
}

/** The person's choice, the scheme it comes to now, and a way to change it. */
export function useTheme(): { preference: ThemePreference; scheme: Scheme; setPreference: (next: ThemePreference) => void } {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [dark, setDark] = useState(systemDark);

  useEffect(() => {
    const media = window.matchMedia?.(QUERY);
    if (!media) return;
    const onChange = () => setDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Other parts of the page (the editor) read the same choice; keep them in step.
  useEffect(() => {
    const onChange = () => setPreferenceState(readPreference());
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    writePreference(next);
    applyPreference(next);
    setPreferenceState(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { preference, scheme: resolveScheme(preference, dark), setPreference };
}

const CHANGE_EVENT = 'portal:theme-change';
